/**
 * wayai communities — the operational side of an organization's communities, for its owners and
 * admins (gh #5003 Decision 7: config goes in files; these are the tasks that are not config).
 *
 * Usage:
 *   wayai communities list [--org <org_id>] [--json]
 *   wayai communities join-link get|rotate|off <community> [--org <org_id>] [-y] [--json]
 *   wayai communities stats <community> [--org <org_id>] [--json]
 *   wayai communities access list|add|remove <community> [<email>…] [--org <org_id>] [-y] [--json]
 *
 * `access` edits the emails an invite-only community admits (gh #5042). They stay commands, never
 * workspace files: they are people's emails. Removing one removes the members it admitted
 * (Decision 5), so `access remove` says so and asks first, as `join-link rotate|off` does.
 *
 * Every request goes to the org-admin routes the web console uses (`/api/communities/*`,
 * Decision 1), which take an interactive sign-in only: a scoped token is refused with 403
 * `AUTHORIZATION_ERROR`, mapped here to a message naming `wayai login` (Decision 2). The client
 * refreshes the login on a 401 (Decision 3). Server text printed for a person is sanitized
 * (Decision 10); `--json` prints the response's `data` as the contract types it.
 */

import { parseArgs } from 'node:util';
import {
  COMMUNITY_ACCESS_ADD_MAX,
  COMMUNITY_ACCESS_FULL_REASON,
  COMMUNITY_ACCESS_MAX,
  COMMUNITY_MEMBER_STATUSES,
  COMMUNITY_STATS_DAYS,
  PEOPLE_SUSPENDED_REASON,
  communityAccessEmailSchema,
  communityIdParam,
  communityOrgParam,
  type CommunityAccessEntry,
  type CommunityJoinLink,
  type CommunityStats,
} from '@wayai/core/contracts';
import { ApiError, type ApiClient } from '../lib/api-client.js';
import { extractApiErrorReason } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { confirm, wantsHelp } from '../lib/utils.js';
import {
  callCommunities as call,
  communitiesClient as client,
  communityNotFound,
  orgNotFound,
} from '../communities/api.js';
import { isInteractive } from '../data/helpers.js';
import { printRows } from '../data/output.js';

const USAGE = `
wayai communities — an organization's communities, for its owners and admins

Usage:
  wayai communities list [--org <org_id>] [--json]
  wayai communities join-link get <community> [--org <org_id>] [--json]
  wayai communities join-link rotate <community> [--org <org_id>] [-y] [--json]
  wayai communities join-link off <community> [--org <org_id>] [-y] [--json]
  wayai communities stats <community> [--org <org_id>] [--json]
  wayai communities access list <community> [--org <org_id>] [--json]
  wayai communities access add <community> <email>... [--org <org_id>] [--json]
  wayai communities access remove <community> <email> [--org <org_id>] [-y] [--json]

<community> is a community id, or its exact name within the organization.
The organization is --org, else this repo's (wayai-ws/wayai.yaml).

join-link get     Print the join link, or that it is off
join-link rotate  Replace the link with a new one and turn it on
join-link off     Turn the link off
stats             Members, joins by source, messages and course completions
access list       Print the emails on the access list, oldest first
access add        Add emails to it, at most ${COMMUNITY_ACCESS_ADD_MAX} at a time
access remove     Take an email off it, and remove from the community anyone it let in

The access list is who the join link admits once the community is invite-only
(settings.admission: invite in community.yaml, or the web console). Emails
match exactly, once trimmed and lowercased.

rotate and off make every link shared before stop working, and access remove
removes members. All three ask first; -y skips the question, and is required
when not run in a terminal.

These commands need a browser sign-in (\`wayai login\`). An API token
(\`wayai login --token\`, or WAYAI_TOKEN) is refused.
`.trim();

const SUBCOMMANDS = ['list', 'join-link', 'stats', 'access'] as const;
type Subcommand = (typeof SUBCOMMANDS)[number];

const JOIN_LINK_ACTIONS = ['get', 'rotate', 'off'] as const;
type JoinLinkAction = (typeof JOIN_LINK_ACTIONS)[number];

const ACCESS_ACTIONS = ['list', 'add', 'remove'] as const;
type AccessAction = (typeof ACCESS_ACTIONS)[number];

interface ParsedArgs {
  positionals: string[];
  org: string | undefined;
  json: boolean;
  yes: boolean;
}

function parse(args: string[]): ParsedArgs {
  try {
    const { values, positionals } = parseArgs({
      args,
      options: { org: { type: 'string' }, json: { type: 'boolean' }, yes: { type: 'boolean', short: 'y' } },
      allowPositionals: true,
      strict: true,
    });
    return { positionals, org: values.org, json: values.json === true, yes: values.yes === true };
  } catch (err) {
    throw usageError(err instanceof Error ? err.message : String(err));
  }
}

function usageError(message: string): Error {
  return expected(`${message}\n\n${USAGE}`);
}

/** Who a selector names: an id is used as is; a name is looked up in an org's list. */
type CommunityTarget = { kind: 'id'; id: string } | { kind: 'name'; name: string; orgId: string };

/** The org from `--org`, else the repo's binding; refused when neither names one. */
function resolveOrgId(flag: string | undefined): string {
  if (flag !== undefined) {
    const parsed = communityOrgParam.safeParse({ orgId: flag });
    if (!parsed.success) throw expected(`Invalid --org ${JSON.stringify(flag)}: expected an organization id (a UUID).`);
    return parsed.data.orgId;
  }
  // Throws when the repo declares an org that cannot be resolved (two files disagreeing), rather
  // than reading that as "none declared".
  const bound = readRepoConfigUnlessBlocked()?.organization_id;
  if (bound) return bound;
  throw expected('No organization: pass --org <org_id>, or run this inside a workspace scoped with `wayai init`.');
}

function targetOf(selector: string, orgFlag: string | undefined): CommunityTarget {
  const asId = communityIdParam.safeParse({ communityId: selector });
  if (asId.success) {
    // An id needs no org, but a malformed --org beside one is refused rather than ignored.
    if (orgFlag !== undefined) resolveOrgId(orgFlag);
    return { kind: 'id', id: asId.data.communityId };
  }
  return { kind: 'name', name: selector, orgId: resolveOrgId(orgFlag) };
}

/** The community a target names, with its name when the lookup learned it. */
async function resolveCommunity(api: ApiClient, target: CommunityTarget): Promise<{ id: string; label: string }> {
  if (target.kind === 'id') return { id: target.id, label: target.id };
  const { data } = await call(api.listOrgCommunities(target.orgId), orgNotFound(target.orgId));
  const matches = data.filter((c) => c.name === target.name);
  if (matches.length === 1) {
    const [c] = matches;
    return { id: c.community_id, label: `"${sanitizeTerminalText(c.name)}" (${c.community_id})` };
  }
  if (matches.length === 0) {
    throw expected(
      `No community named ${JSON.stringify(target.name)} in organization ${target.orgId}. ` +
        'Run `wayai communities list` to see its communities.',
    );
  }
  throw expected(
    `${matches.length} communities in organization ${target.orgId} are named ${JSON.stringify(target.name)}; ` +
      `name one by its id:\n${matches.map((c) => `  ${c.community_id}  created ${c.created_at}`).join('\n')}`,
  );
}

export async function communitiesCommand(args: string[]): Promise<void> {
  if (wantsHelp(args) || args.length === 0) {
    console.log(USAGE);
    return;
  }
  const { positionals, org, json, yes } = parse(args);
  const [sub, ...rest] = positionals;
  // Each refusal names the first mistake: the subcommand and the action are checked before -y's
  // applicability, so a typo is never reported as a misplaced -y.
  if (sub === undefined) throw usageError('No subcommand given.');
  if (!SUBCOMMANDS.includes(sub as Subcommand)) throw usageError(`Unknown subcommand ${JSON.stringify(sub)}.`);
  const [action] = rest;
  if (sub === 'join-link' && !JOIN_LINK_ACTIONS.includes(action as JoinLinkAction)) {
    throw usageError('join-link takes get, rotate or off.');
  }
  if (sub === 'access' && !ACCESS_ACTIONS.includes(action as AccessAction)) {
    throw usageError('access takes list, add or remove.');
  }
  const changesLink = sub === 'join-link' && action !== 'get';
  const asks = changesLink || (sub === 'access' && action === 'remove');
  if (yes && !asks) throw usageError('-y applies only to `join-link rotate`, `join-link off` and `access remove`.');
  switch (sub as Subcommand) {
    case 'list':
      if (rest.length > 0) throw usageError(`Unexpected argument ${JSON.stringify(rest[0])}.`);
      return runList(resolveOrgId(org), json);
    case 'join-link': {
      const [, selector, ...extra] = rest;
      if (selector === undefined || extra.length > 0) throw usageError(`join-link ${action} takes one <community>.`);
      // Refused before any request: off a terminal no one can answer the question (`isInteractive`).
      if (changesLink && !yes && !isInteractive()) {
        throw usageError(`join-link ${action} asks before it changes the link; pass -y when not running in a terminal.`);
      }
      return runJoinLink(action as JoinLinkAction, targetOf(selector, org), { json, yes });
    }
    case 'stats': {
      const [selector, ...extra] = rest;
      if (selector === undefined || extra.length > 0) throw usageError('stats takes one <community>.');
      return runStats(targetOf(selector, org), json);
    }
    case 'access': {
      const [, selector, ...emails] = rest;
      if (selector === undefined) throw usageError(`access ${action} takes a <community>.`);
      if (action === 'list' && emails.length > 0) throw usageError('access list takes one <community>.');
      if (action === 'add' && emails.length === 0) throw usageError('access add takes a <community> and one or more emails.');
      if (action === 'remove' && emails.length !== 1) throw usageError('access remove takes a <community> and one email.');
      // Every email is checked, and all the bad ones named, before any request.
      const valid = accessEmails(emails);
      if (action === 'remove' && !yes && !isInteractive()) {
        throw usageError('access remove asks before it removes anyone; pass -y when not running in a terminal.');
      }
      const target = targetOf(selector, org);
      if (action === 'list') return runAccessList(target, json);
      if (action === 'add') return runAccessAdd(target, valid, json);
      return runAccessRemove(target, valid[0], { json, yes });
    }
  }
}

async function runList(orgId: string, json: boolean): Promise<void> {
  const api = await client();
  const { data } = await call(api.listOrgCommunities(orgId), orgNotFound(orgId));
  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  if (data.length === 0) {
    console.log(`Organization ${orgId} owns no communities.`);
    return;
  }
  console.log(`Communities of ${sanitizeTerminalText(data[0].org_name)} (${orgId})\n`);
  printRows(data.map((c) => ({ name: c.name, community_id: c.community_id, created_at: c.created_at })));
}

async function runJoinLink(
  action: JoinLinkAction,
  target: CommunityTarget,
  { json, yes }: { json: boolean; yes: boolean },
): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const notFound = communityNotFound(label);

  if (action === 'get') {
    const { data } = await call(api.getCommunityJoinLink(id), notFound);
    printLink(data, label, json);
    return;
  }

  await warnThenAsk(
    [
      action === 'rotate'
        ? `This replaces the join link of ${label} with a new one and turns it on.`
        : `This turns off the join link of ${label}.`,
      'Every link shared before stops working.',
    ],
    yes,
    'Aborted: the join link is unchanged.',
  );
  const { data } = await call(
    action === 'rotate' ? api.replaceCommunityJoinLink(id) : api.disableCommunityJoinLink(id),
    notFound,
  );
  printLink(data, label, json);
}

/**
 * A change that cannot be taken back: its warning on stderr — printed whether or not -y skips the
 * question, and kept off stdout so `--json` stays parseable — then the question unless -y.
 */
async function warnThenAsk(warning: string[], yes: boolean, aborted: string): Promise<void> {
  for (const line of warning) console.error(line);
  if (!yes && !(await confirm('Proceed?'))) throw expected(aborted);
}

function printLink(link: CommunityJoinLink, label: string, json: boolean): void {
  if (json) {
    console.log(JSON.stringify(link, null, 2));
    return;
  }
  if (link.enabled && link.url) {
    console.log(`The join link of ${label} is on:`);
    console.log(sanitizeTerminalText(link.url));
    return;
  }
  console.log(`The join link of ${label} is off. \`wayai communities join-link rotate\` makes a new one.`);
}

async function runStats(target: CommunityTarget, json: boolean): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const { data } = await call(api.getCommunityStats(id), communityNotFound(label));
  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  printStats(data, label);
}

function printStats(stats: CommunityStats, label: string): void {
  const { members, joins } = stats;
  const sum = (key: 'messages' | 'posts' | 'replies') => stats.days.reduce((total, day) => total + day[key], 0);
  const [chat, posts, replies] = [sum('messages'), sum('posts'), sum('replies')];

  console.log(`Stats of ${label}\n`);
  console.log(`Members       ${COMMUNITY_MEMBER_STATUSES.map((status) => `${members[status]} ${status}`).join(' · ')}`);
  console.log(`Active        ${stats.active_members_7d} in the last 7 days`);
  console.log(`Joins         ${joins.joins_30d} in the last 30 days · ${joins.joins_7d} in the last 7`);
  console.log(`New accounts  ${joins.new_accounts_30d} in the last 30 days · ${joins.new_accounts_7d} in the last 7 (made in the join flow)`);
  console.log(
    `Messages      ${chat + posts + replies} in the last ${COMMUNITY_STATS_DAYS} days (${chat} chat · ${posts} posts · ${replies} replies)`,
  );

  console.log('\nJoins by source tag, last 30 days');
  if (stats.joins_by_source.length === 0) {
    console.log('  Nobody joined in the last 30 days.');
  } else {
    printRows(stats.joins_by_source.map((s) => ({
      source: s.source ?? '(no tag)',
      joins_7d: s.joins_7d,
      joins_30d: s.joins_30d,
      new_accounts_30d: s.new_accounts_30d,
    })));
  }

  // Optional on the contract (it arrived after the field's first readers); the server always sets it.
  const courses = stats.courses ?? [];
  if (courses.length === 0) return;
  console.log('\nCourse completions, per lesson');
  for (const course of courses) {
    console.log(`  ${sanitizeTerminalText(course.title)}${course.published ? '' : ' (draft)'}`);
    for (const lesson of course.lessons) {
      console.log(`    ${String(lesson.completions).padStart(6)}  ${sanitizeTerminalText(lesson.title)}${lesson.published ? '' : ' (draft)'}`);
    }
  }
}

// ── access: the emails an invite-only community admits (gh #5042) ──

/**
 * The emails as the access list holds them (`communityAccessEmailSchema`: trimmed and lowercased),
 * each once, in the order given. Refused, naming every value that is not an email, and past the
 * emails one add takes.
 */
function accessEmails(raw: string[]): string[] {
  const emails = new Set<string>();
  const invalid: string[] = [];
  for (const value of raw) {
    const parsed = communityAccessEmailSchema.safeParse(value);
    if (parsed.success) emails.add(parsed.data);
    else invalid.push(JSON.stringify(sanitizeTerminalText(value)));
  }
  if (invalid.length > 0) {
    throw expected(`Not ${invalid.length === 1 ? 'an email' : 'emails'}: ${invalid.join(', ')}. Nothing was sent.`);
  }
  if (emails.size > COMMUNITY_ACCESS_ADD_MAX) {
    throw expected(`An add takes at most ${COMMUNITY_ACCESS_ADD_MAX} emails, and ${emails.size} were given. Nothing was sent.`);
  }
  return [...emails];
}

/** An access-list write, with the routes' own 409s mapped too: the list's cap, and a suspended account (Decision 8). */
async function callAccess<T>(request: Promise<T>, notFound: string, label: string): Promise<T> {
  try {
    return await call(request, notFound);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const reason = extractApiErrorReason(err);
      if (reason === COMMUNITY_ACCESS_FULL_REASON) {
        throw expected(`Nothing was added: the access list of ${label} would hold more than ${COMMUNITY_ACCESS_MAX} emails.`);
      }
      if (reason === PEOPLE_SUSPENDED_REASON) {
        throw expected('WayAI has suspended this account, so it cannot change a community\'s access list.');
      }
    }
    throw err;
  }
}

/**
 * The access list a page at a time, oldest first: one request per page, read only as far as the
 * caller goes — every request counts against the per-person budget all community routes share.
 */
async function* accessPages(api: ApiClient, id: string, notFound: string): AsyncGenerator<CommunityAccessEntry[]> {
  let cursor: string | undefined;
  do {
    const { data } = await call(api.listCommunityAccess(id, cursor), notFound);
    yield data.entries;
    cursor = data.next_cursor ?? undefined;
  } while (cursor !== undefined);
}

async function runAccessList(target: CommunityTarget, json: boolean): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const entries: CommunityAccessEntry[] = [];
  for await (const page of accessPages(api, id, communityNotFound(label))) entries.push(...page);
  if (json) {
    console.log(JSON.stringify(entries, null, 2));
    return;
  }
  if (entries.length === 0) {
    console.log(`The access list of ${label} is empty.`);
    return;
  }
  console.log(`The access list of ${label}: ${entries.length} email(s), oldest first\n`);
  printRows(entries.map((entry) => ({ email: entry.email, added: entry.created_at })));
}

async function runAccessAdd(target: CommunityTarget, emails: string[], json: boolean): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  const { data } = await callAccess(api.addCommunityAccess(id, { emails }), communityNotFound(label), label);
  if (json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  const { added, already_listed: already } = data;
  const listed = already === 0 ? '' : `; ${already} ${already === 1 ? 'was' : 'were'} already on it`;
  console.log(`Added ${added.length} email(s) to the access list of ${label}${listed}.`);
}

async function runAccessRemove(
  target: CommunityTarget,
  email: string,
  { json, yes }: { json: boolean; yes: boolean },
): Promise<void> {
  const api = await client();
  const { id, label } = await resolveCommunity(api, target);
  let entry: CommunityAccessEntry | undefined;
  for await (const page of accessPages(api, id, communityNotFound(label))) {
    entry = page.find((e) => e.email === email);
    if (entry) break;
  }
  if (!entry) throw expected(`${email} is not on the access list of ${label}. Nothing was removed.`);

  await warnThenAsk(
    [`This takes ${email} off the access list of ${label}.`, 'Anyone this email let in is removed from the community now.'],
    yes,
    'Aborted: the access list is unchanged.',
  );
  await callAccess(
    api.removeCommunityAccess(id, entry.access_id),
    `${email} is no longer on the access list of ${label}, or you are not an owner or admin of its organization. Nothing was removed.`,
    label,
  );
  if (json) {
    console.log(JSON.stringify(entry, null, 2));
    return;
  }
  console.log(`Removed ${email} from the access list of ${label}, and from the community anyone it let in.`);
}
